import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import Navigation from '../components/Navigation'
import ApiKeyManagement from '../components/ApiKeyManagement'
import { useAuth } from '../context/AuthContext'
import { apiCall, getApiBaseUrl } from '../utils/api'
import { formatUSD } from '../utils/currency'
import { useToast } from '../context/ToastContext'
import StripeCheckoutModal from '../components/modals/StripeCheckoutModal'
import PricingPackagesModal from '../components/modals/PricingPackagesModal'
import AutoReloadSettings from '../components/AutoReloadSettings'
import AddPaymentMethodModal from '../components/modals/AddPaymentMethodModal'
import { CREDIT_PACKAGES } from '../components/pricing/PricingPackages'

export default function ProfilePage() {
  const { user, loading, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { showToast } = useToast()
  const [activeTab, setActiveTab] = useState('account')
  const [profileData, setProfileData] = useState(null)
  const [billingHistory, setBillingHistory] = useState([])
  const [usageHistory, setUsageHistory] = useState([])
  const [autoReloadSettings, setAutoReloadSettings] = useState({
    enabled: false,
    threshold: 10,
    packageId: ''
  })
  const [showCheckoutModal, setShowCheckoutModal] = useState(false)
  const [showPricingPackagesModal, setShowPricingPackagesModal] = useState(false)
  const [showAddPaymentMethodModal, setShowAddPaymentMethodModal] = useState(false)
  const [selectedPackage, setSelectedPackage] = useState(null)
  const [paymentMethods, setPaymentMethods] = useState([])
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null)
  const [legalConsent, setLegalConsent] = useState(false)

  useEffect(() => {
    if (!loading && !user) {
      navigate('/')
      return
    }

    if (user) {
      loadProfileData()
    }
  }, [user, loading, navigate])

  useEffect(() => {
    // Handle URL hash for tab navigation
    const hash = location.hash.substring(1)

    // Handle special case: security-api-keys (from dropdown)
    if (hash === 'security-api-keys') {
      setActiveTab('security')

      // Scroll to Create API Key section
      setTimeout(() => {
        const targetElement = document.getElementById('create-api-key')
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
      return
    }

    // Handle regular tab navigation
    const validTabs = ['account', 'billing', 'autoreload', 'usage', 'security', 'paymentmethods']
    if (hash && validTabs.includes(hash)) {
      setActiveTab(hash)

      // Smooth scroll to content area for regular tab clicks
      setTimeout(() => {
        const targetElement = document.querySelector('.lg\\:col-span-2')
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }, [location])

  const loadProfileData = async () => {
    try {
      const [userResponse, statsResponse] = await Promise.all([
        apiCall('/v1/user/me'),
        apiCall('/v1/user/billing-stats')
      ])

      if (userResponse.ok && statsResponse.ok) {
        const userData = await userResponse.json()
        const statsData = await statsResponse.json()

        setProfileData({
          user: userData,
          stats: statsData
        })

        // Set auto-reload settings if they exist
        if (userData.auto_reload_enabled !== undefined) {
          setAutoReloadSettings({
            enabled: userData.auto_reload_enabled,
            threshold: userData.auto_reload_threshold_usd || 10,
            packageId: '' // Will be set after loading packages
          })
        }
      } else {
        console.error('Failed to load profile data:', userResponse.status, statsResponse.status)
        showToast('error', 'Failed to load profile data', 'Error')
      }
    } catch (error) {
      console.error('Error loading profile data:', error)
      showToast('error', 'Failed to load profile data', 'Error')
    }
  }

  const loadBillingHistory = async () => {
    try {
      const response = await apiCall('/v1/user/billing-history?limit=10&type=credit')
      if (response.ok) {
        const data = await response.json()
        setBillingHistory(data.transactions || [])
      } else {
        console.error('Failed to load billing history:', response.status)
        setBillingHistory([])
      }
    } catch (error) {
      console.error('Error loading billing history:', error)
      setBillingHistory([])
    }
  }

  const loadUsageHistory = async () => {
    try {
      const response = await apiCall('/v1/user/billing-history?limit=20&type=charge')
      if (response.ok) {
        const data = await response.json()
        setUsageHistory(data.transactions || [])
      } else {
        console.error('Failed to load usage history:', response.status)
        setUsageHistory([])
      }
    } catch (error) {
      console.error('Error loading usage history:', error)
      setUsageHistory([])
    }
  }

  const loadPaymentMethods = async () => {
    try {
      const response = await apiCall('/v1/user/payment-methods')
      if (response.ok) {
        const data = await response.json()
        setPaymentMethods(data.paymentMethods || [])

        // Auto-select the first active payment method
        if (data.paymentMethods && data.paymentMethods.length > 0) {
          setSelectedPaymentMethod(data.paymentMethods[0].id)
        }
      } else {
        console.error('Failed to load payment methods:', response.status)
        setPaymentMethods([])
      }
    } catch (error) {
      console.error('Error loading payment methods:', error)
      setPaymentMethods([])
    }
  }

  const handleTabChange = (tab) => {
    setActiveTab(tab)
    navigate(`#${tab}`)

    // Load tab-specific data
    if (tab === 'billing' && billingHistory.length === 0) {
      loadBillingHistory()
    } else if (tab === 'usage' && usageHistory.length === 0) {
      loadUsageHistory()
    } else if (tab === 'paymentmethods' && paymentMethods.length === 0) {
      loadPaymentMethods()
    } else if (tab === 'autoreload' && paymentMethods.length === 0) {
      loadPaymentMethods()
    }

    // Smooth scroll to content area (but NOT to specific sections)
    setTimeout(() => {
      const targetElement = document.querySelector('.lg\\:col-span-2')
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 100)
  }

  const handleBuyCredits = async (pkg) => {
    if (!pkg) return

    try {
      const apiUrl = getApiBaseUrl()

      // Call API to create Stripe Checkout session
      const response = await fetch(`${apiUrl}/v1/payment/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify({
          credit_package_id: pkg.id,
          success_url: `${window.location.origin}/payment/success`,
          cancel_url: `${window.location.origin}/payment/cancel`,
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to create checkout session')
      }

      const data = await response.json()

      // Redirect to Stripe Checkout
      if (data.checkout_url) {
        window.location.href = data.checkout_url
      } else {
        showToast('error', 'Checkout URL not provided', 'Error')
      }
    } catch (error) {
      console.error('Checkout error:', error)
      showToast('error', error.message, 'Checkout Failed')
    }
  }

  const handleSaveAutoReload = async () => {
    try {
      // Validate payment method if enabling auto-reload
      if (autoReloadSettings.enabled && paymentMethods.length === 0) {
        showToast('error', 'Please add a payment method before enabling auto-reload', 'Payment Method Required')
        return
      }

      // Validate legal consent
      if (autoReloadSettings.enabled && !legalConsent) {
        showToast('error', 'Please accept the auto-reload authorization to enable this feature', 'Consent Required')
        return
      }

      const selectedPkg = CREDIT_PACKAGES.find(p => p.id === autoReloadSettings.packageId)
      const amountUsd = selectedPkg ? selectedPkg.credits : 20

      await apiCall('/v1/user/auto-reload', {
        method: 'PUT',
        body: JSON.stringify({
          enabled: autoReloadSettings.enabled,
          threshold_usd: parseFloat(autoReloadSettings.threshold),
          amount_usd: amountUsd,
          package_id: autoReloadSettings.packageId
        })
      })

      showToast('success', 'Auto-reload settings saved successfully!', 'Settings Saved')
      loadProfileData()
    } catch (error) {
      console.error('Error saving auto-reload settings:', error)
      showToast('error', 'Failed to save settings. Please try again.', 'Error')
    }
  }

  // Redirect to home if not authenticated
  if (!loading && !user) {
    return null
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Navigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile Header */}
        <div className="bg-primary-600 text-white rounded-xl shadow-xl p-8 mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-4xl overflow-hidden border-4 border-white border-opacity-30">
                {profileData?.user?.picture || user?.picture ? (
                  <img src={profileData?.user?.picture || user?.picture} alt="Profile" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                ) : (
                  <i className="fas fa-user"></i>
                )}
              </div>
              <div>
                <h1 className="text-3xl font-bold mb-1">
                  {profileData?.user?.name || profileData?.user?.username || user?.name || 'User Profile'}
                </h1>
                <p className="text-primary-100">{profileData?.user?.email || user?.email}</p>
                <p className="text-sm text-primary-200 mt-1">
                  Member since: {profileData?.user?.createdAt ? new Date(profileData.user.createdAt).getFullYear() : '2025'}
                </p>
              </div>
            </div>
            <div className="text-right bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-6 min-w-[250px]">
              <div className="text-4xl font-bold mb-2">
                {formatUSD(profileData?.user?.account_balance_usd)}
              </div>
              <div className="text-sm opacity-90 mb-4">Current Balance</div>
              <button
                onClick={() => setShowPricingPackagesModal(true)}
                className="w-full bg-white text-primary-600 hover:bg-gray-100 text-sm font-semibold py-2 rounded-lg transition-colors shadow-lg"
              >
                💳 Buy Credits
              </button>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Settings Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 border border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Settings</h2>
              <nav className="space-y-2">
                {[
                  { id: 'account', icon: 'fas fa-user-circle', label: 'Account Info' },
                  { id: 'billing', icon: 'fas fa-credit-card', label: 'Billing & Credits' },
                  { id: 'autoreload', icon: 'fas fa-sync', label: 'Auto-Reload' },
                  { id: 'usage', icon: 'fas fa-chart-line', label: 'Usage History' },
                  { id: 'security', icon: 'fas fa-shield-alt', label: 'Security' },
                  { id: 'paymentmethods', icon: 'fas fa-wallet', label: 'Payment Methods' }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`block w-full text-left px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                      activeTab === tab.id
                        ? 'bg-gray-100 dark:bg-gray-700 text-primary-600 dark:text-primary-400'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <i className={`${tab.icon} mr-2`}></i>
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Settings Content */}
          <div className="lg:col-span-2">
            {/* Account Info Tab */}
            {activeTab === 'account' && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8 border border-gray-200 dark:border-gray-700">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Account Information</h2>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email</label>
                    <input
                      type="email"
                      value={profileData?.user?.email || user?.email || ''}
                      readOnly
                      className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Username</label>
                    <input
                      type="text"
                      value={profileData?.user?.username || profileData?.user?.email?.split('@')[0] || user?.email?.split('@')[0] || ''}
                      readOnly
                      className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Account Type</label>
                    <div className="px-4 py-2 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg text-primary-700 dark:text-primary-400 font-semibold">
                      Pay-As-You-Go (Prepaid Credits)
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Account Stats</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-gray-900 dark:text-white">
                          {formatUSD(profileData?.stats?.total_spent_usd)}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Lifetime Spend</div>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-gray-900 dark:text-white">
                          {formatUSD(profileData?.stats?.total_credits_purchased_usd)}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Total Credits Purchased</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Billing & Credits Tab */}
            {activeTab === 'billing' && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8 border border-gray-200 dark:border-gray-700">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Billing & Credits</h2>

                <div className="space-y-6">
                  {/* Current Balance */}
                  <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Current Balance</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Available credits for processing</p>
                      </div>
                      <div className="text-4xl font-bold text-green-700 dark:text-green-400">
                        {formatUSD(profileData?.user?.account_balance_usd)}
                      </div>
                    </div>
                  </div>

                  {/* Recent Purchases */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Credit Purchases</h3>
                    {billingHistory.length === 0 ? (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        <i className="fas fa-receipt text-4xl mb-4 text-gray-300 dark:text-gray-600"></i>
                        <p className="text-lg font-semibold mb-2">No purchases yet</p>
                        <p className="text-sm">Purchase credits to get started with ContentIndexer</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {billingHistory.map((tx) => (
                          <div key={tx.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center">
                                <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mr-3">
                                  <i className="fas fa-credit-card text-primary-600 dark:text-primary-400"></i>
                                </div>
                                <div>
                                  <div className="font-semibold text-gray-900 dark:text-white">{tx.description}</div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {new Date(tx.created_at).toLocaleString(undefined, {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric',
                                      hour: 'numeric',
                                      minute: '2-digit',
                                      hour12: true
                                    })}
                                    {tx.metadata && JSON.parse(tx.metadata).is_auto_reload && (
                                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                                        Auto-reload
                                      </span>
                                    )}
                                    {(!tx.metadata || !JSON.parse(tx.metadata).is_auto_reload) && (
                                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                                        Manual
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-2xl font-bold text-green-700 dark:text-green-400">
                                  +{formatUSD(Math.abs(tx.amount_usd))}
                                </div>
                                {tx.credit_package_bonus_percentage > 0 && (
                                  <div className="text-xs text-primary-600 dark:text-primary-400 font-semibold">
                                    +{tx.credit_package_bonus_percentage}% bonus
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Quick Purchase */}
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Quick Top-Up</h3>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      {CREDIT_PACKAGES.slice(1, 3).map(pkg => (
                        <button
                          key={pkg.id}
                          onClick={() => handleBuyCredits(pkg)}
                          className={`px-4 py-3 rounded-lg font-semibold transition-colors ${
                            pkg.popular
                              ? 'bg-primary-600 hover:bg-primary-700 text-white'
                              : pkg.bestValue
                              ? 'bg-primary-600 hover:bg-primary-700 text-white shadow-lg'
                              : 'bg-gray-600 hover:bg-gray-700 text-white'
                          }`}
                        >
                          ${pkg.price} → ${pkg.credits} <span className="text-xs">(+{Math.round((pkg.bonus / pkg.price) * 100)}%)</span>
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => setShowPricingPackagesModal(true)}
                      className="w-full border-2 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white px-4 py-3 rounded-lg font-semibold hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      View All Packages
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Auto-Reload Tab */}
            {activeTab === 'autoreload' && (
              <AutoReloadSettings />
            )}

            {/* Usage History Tab */}
            {activeTab === 'usage' && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8 border border-gray-200 dark:border-gray-700">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Usage History</h2>

                {usageHistory.length === 0 ? (
                  <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                    <i className="fas fa-chart-bar text-4xl mb-4 text-gray-300 dark:text-gray-600"></i>
                    <p className="text-lg font-semibold mb-2">No usage yet</p>
                    <p className="text-sm">Process content to see your usage history here</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {usageHistory.map((tx) => (
                      <div key={tx.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-900/50 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center flex-1">
                            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mr-3">
                              <i className="fas fa-play-circle text-blue-600 dark:text-blue-400"></i>
                            </div>
                            <div className="flex-1">
                              <div className="font-semibold text-gray-900 dark:text-white">{tx.description}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                <i className="fas fa-clock mr-1"></i>
                                {new Date(tx.created_at).toLocaleDateString()}
                              </div>
                              {tx.job_id && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  Job ID: {tx.job_id.substring(0, 8)}...
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-right ml-4">
                            <div className="text-xl font-bold text-red-600 dark:text-red-400">
                              -{formatUSD(Math.abs(tx.amount_usd))}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">Usage Charge</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8 border border-gray-200 dark:border-gray-700">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Security Settings</h2>

                <div className="space-y-6">
                  {/* Active Sessions */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Active Sessions</h3>
                    <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex items-center justify-between">
                      <div className="flex items-center">
                        <i className="fas fa-check-circle text-green-600 dark:text-green-400 text-xl mr-3"></i>
                        <div>
                          <div className="font-semibold text-gray-900 dark:text-white">Current Session</div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">Started today</div>
                        </div>
                      </div>
                      <button
                        onClick={logout}
                        className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                      >
                        End Session
                      </button>
                    </div>
                  </div>

                  {/* API Keys */}
                  <div id="api-access">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">API Access</h3>
                    <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        Manage your API keys for programmatic access
                      </p>
                      <ApiKeyManagement />
                    </div>
                  </div>

                  {/* Account Actions */}
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-4">Danger Zone</h3>
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                        Permanently delete your account and all associated data
                      </p>
                      <button
                        onClick={() => showToast('info', 'Account deletion requires email confirmation. Feature coming soon.', 'Coming Soon')}
                        className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
                      >
                        <i className="fas fa-exclamation-triangle mr-2"></i>
                        Delete Account
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Methods Tab */}
            {activeTab === 'paymentmethods' && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8 border border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Payment Methods</h2>
                  <button
                    onClick={() => setShowAddPaymentMethodModal(true)}
                    className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold transition-colors flex items-center gap-2"
                  >
                    <span>+</span>
                    Add Payment Method
                  </button>
                </div>

                {paymentMethods.length === 0 ? (
                  /* Empty State */
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">💳</div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      No Payment Methods Yet
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                      Add a payment method to enable quick checkout and automatic reloading
                    </p>
                    <button
                      onClick={() => setShowAddPaymentMethodModal(true)}
                      className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold transition-colors inline-flex items-center gap-2"
                    >
                      <span>+</span>
                      Add Your First Payment Method
                    </button>
                  </div>
                ) : (
                  /* Payment Methods List */
                  <div className="space-y-3">
                    {paymentMethods.map((pm) => (
                      <div
                        key={pm.id}
                        onClick={async () => {
                          if (!pm.is_default) {
                            try {
                              const response = await apiCall(`/v1/user/payment-methods/${pm.id}/default`, { method: 'PUT' })
                              if (response.ok) {
                                showToast('success', 'Default payment method updated', 'Success')
                                loadPaymentMethods()
                              } else {
                                showToast('error', 'Failed to set default payment method', 'Error')
                              }
                            } catch (error) {
                              showToast('error', 'Failed to set default payment method', 'Error')
                            }
                          }
                        }}
                        className={`group relative border rounded-lg p-4 transition-all ${
                          pm.is_default
                            ? 'bg-primary-50 dark:bg-gray-800 border-primary-400 dark:border-primary-600'
                            : 'bg-white dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 hover:border-primary-400 dark:hover:border-primary-500 cursor-pointer'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          {/* Left: Radio + Card Info */}
                          <div className="flex items-center gap-3">
                            {/* Radio Button */}
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                              pm.is_default
                                ? 'border-primary-600 dark:border-primary-500'
                                : 'border-gray-300 dark:border-gray-600 group-hover:border-primary-400'
                            }`}>
                              {pm.is_default && (
                                <div className="w-3 h-3 rounded-full bg-primary-600 dark:bg-primary-500"></div>
                              )}
                            </div>

                            {/* Card Details */}
                            <div>
                              <div className="flex items-center gap-2">
                                <span className={`font-semibold ${pm.is_default ? 'text-gray-900 dark:text-white' : 'text-gray-900 dark:text-white'}`}>
                                  {pm.card_brand?.charAt(0).toUpperCase() + pm.card_brand?.slice(1) || 'Card'} •••• {pm.card_last4}
                                </span>
                                {pm.is_default && (
                                  <span className="text-xs font-medium text-primary-700 dark:text-primary-400">
                                    Default
                                  </span>
                                )}
                              </div>
                              <div className={`text-sm ${pm.is_default ? 'text-gray-700 dark:text-gray-400' : 'text-gray-500 dark:text-gray-400'}`}>
                                Expires {pm.card_exp_month?.toString().padStart(2, '0')}/{pm.card_exp_year}
                              </div>
                            </div>
                          </div>

                          {/* Right: Remove Button */}
                          <button
                            onClick={async (e) => {
                              e.stopPropagation() // Prevent triggering the card selection

                              const confirmMessage = pm.is_default
                                ? 'Remove this default payment method? The next card will be set as default.'
                                : 'Remove this payment method?'

                              if (confirm(confirmMessage)) {
                                try {
                                  const response = await apiCall(`/v1/user/payment-methods/${pm.id}`, { method: 'DELETE' })
                                  if (response.ok) {
                                    showToast('success', 'Payment method removed', 'Success')
                                    loadPaymentMethods()
                                  } else {
                                    showToast('error', 'Failed to remove payment method', 'Error')
                                  }
                                } catch (error) {
                                  showToast('error', 'Failed to remove payment method', 'Error')
                                }
                              }
                            }}
                            className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-opacity p-2"
                            aria-label="Remove payment method"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Info Card */}
                {paymentMethods.length > 0 && (
                  <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="text-2xl">🔒</div>
                      <div className="text-sm text-blue-900 dark:text-blue-200">
                        <strong>Payment Method Security</strong>
                        <ul className="mt-2 space-y-1">
                          <li>✓ All payment information is securely stored by Stripe</li>
                          <li>✓ We never store your full card number or CVV</li>
                          <li>✓ You can add or remove payment methods at any time</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Payment Method Modal */}
      {showAddPaymentMethodModal && (
        <AddPaymentMethodModal
          onClose={() => setShowAddPaymentMethodModal(false)}
          onSuccess={() => {
            setShowAddPaymentMethodModal(false)
            loadPaymentMethods()
            showToast('success', 'Payment method added successfully', 'Success')
          }}
        />
      )}

      {/* Stripe Checkout Modal */}
      {showCheckoutModal && (
        <StripeCheckoutModal
          onClose={() => {
            setShowCheckoutModal(false)
            setSelectedPackage(null)
            loadProfileData()
            loadBillingHistory()
          }}
          package={selectedPackage}
        />
      )}

      {/* Pricing Packages Modal */}
      {showPricingPackagesModal && (
        <PricingPackagesModal
          onClose={() => {
            setShowPricingPackagesModal(false)
            loadProfileData()
            loadBillingHistory()
          }}
        />
      )}
    </div>
  )
}
